import i18n from "../../shared/i18n";
import {
  $selectedPersonTypesQty,
  $selectedPersonTypes,
  selectedPersonTypesApi,
  SelectedPersonTypes,
} from "../../store/selected/selectedPersonTypes";
import { $selectedLocation } from "../../store/selected/selectedLocations";
import { $selectedPackages } from "../../store/selected/selectedPackages";
import { $selectedActivities } from "../../store/selected/selectedActivities";
import uniq from "lodash/uniq";
import cloneDeep from "lodash/cloneDeep";
import format from "date-fns/format";
import { isArrayFilled } from "../../utils";

export const validateMainStep = async (showErrors = true): Promise<boolean> => {
  const selectedLocation = $selectedLocation.getState();
  if (!selectedLocation) {
    if (showErrors) alert(i18n.t("step_Main:invalidStepMessages.location"));
    return false;
  }

  const selectedPersonTypesQty = $selectedPersonTypesQty.getState();
  if (selectedPersonTypesQty === 0) {
    if (showErrors) alert(i18n.t("step_Main:invalidStepMessages.personTypes"));
    return false;
  }

  const packages = $selectedPackages.getState();
  const activities = $selectedActivities.getState();

  const productsSelected = packages.length > 0 || activities.length > 0;
  if (!productsSelected) {
    if (showErrors) alert(i18n.t("step_Main:invalidStepMessages.products"));
    return false;
  }

  const dates: string[] = [];
  const formatDate = (v: Date) => format(v, "d MMMM");

  packages.forEach((package_) => {
    dates.push(formatDate(package_.availabilitySlot.date));
  });
  activities.forEach((activity) => {
    dates.push(formatDate(activity.availabilitySlot.date));
  });

  const uniqDates = uniq(dates);
  if (uniqDates.length > 1) {
    if (showErrors) {
      return window.confirm(i18n.t("step_Main:invalidStepMessages.dates"));
    }
  }

  const unallocatedPersonTypes = calcUnallocatedPersonTypes();

  if (unallocatedPersonTypes) {
    if (showErrors) {
      const message = unallocatedPersonTypes.reduce<string>(
        (acc, { name, quantity }) => {
          return acc + `\n${name}: ${quantity}`;
        },
        ""
      );
      const result = window.confirm(
        i18n.t("step_Main:invalidStepMessages.unallocatedPersonTypes", {
          v: message,
        })
      );

      if (result) {
        const selectedPersonTypes = $selectedPersonTypes.getState();

        const newSelectedPersonTypes = selectedPersonTypes.reduce<SelectedPersonTypes>(
          (acc, personType) => {
            const unallocatedPersonType = unallocatedPersonTypes.find(
              ({ id }) => id === personType.id
            );

            const quantity = unallocatedPersonType
              ? personType.quantity - unallocatedPersonType.quantity
              : personType.quantity;

            if (quantity <= 0) return [...acc];

            return [
              ...acc,
              {
                ...personType,
                quantity: quantity,
              },
            ];
          },
          []
        );

        selectedPersonTypesApi.set(newSelectedPersonTypes);
      }

      return result;
    }
  }

  return true;
};

export const calcUnallocatedPersonTypes = ():
  | { id: number; quantity: number; name: string }[]
  | null => {
  const packages = $selectedPackages.getState();
  const activities = $selectedActivities.getState();
  const selectedPersonTypes = $selectedPersonTypes.getState();

  const allPersonTypes = [
    ...packages.map(({ personTypes }) => cloneDeep(personTypes)),
    ...activities.map(({ personTypes }) => cloneDeep(personTypes)),
  ];

  const allocatedPersonTypes = allPersonTypes.reduce<
    { id: number; quantity: number; name: string }[]
  >((acc, personTypes) => {
    const accCloned = [...acc];

    personTypes.forEach((personType) => {
      const existingPersonType = accCloned.find(
        ({ id }) => id === personType.id
      );
      if (existingPersonType) {
        existingPersonType.quantity += personType.quantity;
      } else {
        accCloned.push(personType);
      }
    });

    return accCloned;
  }, []);

  const unallocatedPersonTypes: {
    id: number;
    quantity: number;
    name: string;
  }[] = [];

  selectedPersonTypes.forEach((selectedPersonType) => {
    const allocatedPersonType = allocatedPersonTypes.find(
      ({ id }) => id === selectedPersonType.id
    );

    if (allocatedPersonType) {
      if (allocatedPersonType.quantity < selectedPersonType.quantity) {
        unallocatedPersonTypes.push({
          ...selectedPersonType,
          quantity: selectedPersonType.quantity - allocatedPersonType.quantity,
        });
      }
    } else {
      unallocatedPersonTypes.push(selectedPersonType);
    }
  });

  return isArrayFilled(unallocatedPersonTypes) ? unallocatedPersonTypes : null;
};
